import React, { useMemo } from 'react';
import { Typography, Box, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ResultDropZone from '../../DropZones/ResultDropZone';
import { useDispatch, useSelector } from 'react-redux';
import ResultCard from './ResultCard';
import { isSimilarDataType } from '../../../CommonFunctions/CommonFunctions';
import {
  addResultForIfElse,
  setResultTypeForIfElse,
} from '../../../../redux/Calculations/CalculationsActions';
import { fontSize, palette } from '../../../..';
import { isFlowUsed } from '../../utils';
import { RootState } from '../../../../redux';

const Result = () => {
  const valueTypes = useMemo(() => ['text', 'decimal', 'integer', 'boolean', 'date'], []);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const dispatch = useDispatch();
  const { propKey, calculations } = useSelector((state: RootState) => ({
    propKey:
      state.tabTileProps.selectedTabId.toString() +
      '.' +
      state.tabTileProps.selectedTileId.toString(),
    calculations: state.calculations,
  }));

  const currentCalculationSession = calculations.properties[propKey].currentCalculationSession;
  const disable = useMemo(() => {
    if (!currentCalculationSession) return true;
    return isFlowUsed(
      Object.keys(currentCalculationSession.calculationInfo.flows),
      currentCalculationSession.calculationInfo.flows,
      currentCalculationSession.activeFlow!,
      currentCalculationSession.calculationInfo,
    );
  }, [currentCalculationSession]);

  if (
    !currentCalculationSession ||
    !currentCalculationSession.activeFlow ||
    currentCalculationSession.activeCondition === null
  )
    return null;

  const conditionNumber = currentCalculationSession.activeCondition;
  const selectedFlow =
    currentCalculationSession.calculationInfo.flows[currentCalculationSession.activeFlow][
      conditionNumber!
    ];
  const selectedResultType =
    currentCalculationSession.calculationInfo.selectedResultType[
      currentCalculationSession.activeFlow
    ];

  const onlyConditionWithResultField =
    currentCalculationSession.calculationInfo.flows[currentCalculationSession.activeFlow].filter(
      (condition: any) =>
        condition?.source?.length === 1 && condition?.flowId !== selectedFlow?.flowId,
    ).length === 0;
  const hasSource = selectedFlow?.source?.length > 0;

  const safeToAddType = (type: string): boolean => {
    if (disable) return false;
    if (selectedFlow?.condition !== 'Else') {
      if (!selectedResultType || onlyConditionWithResultField) return true;
      if (['integer', 'decimal'].includes(selectedResultType)) {
        if (selectedResultType === type.toLowerCase()) return true;
        else return false;
      }
      return isSimilarDataType(selectedResultType, type);
    }
    return false;
  };

  const activeFlow = currentCalculationSession?.activeFlow;

  const isFlowUsedInOtherFlowsAsSource = (flowId: string) => {
    const allFlowsWithFlowAsSource = Object.keys(
      currentCalculationSession.calculationInfo.flows,
    ).filter((flow: any) =>
      currentCalculationSession.calculationInfo.flows[flow][0].sourceType.includes('flow'),
    );
    const isUsedInFlows = allFlowsWithFlowAsSource.some((flow) =>
      currentCalculationSession.calculationInfo.flows[flow].some((f: any) =>
        f.source.includes(flowId),
      ),
    );

    const isUsedInFilters = Object.values(
      currentCalculationSession.calculationInfo.conditionFilters,
    ).some((filter: any) =>
      filter[0].conditions.some(
        (condition: any) =>
          condition.leftOperand[0] === flowId || condition.rightOperand.includes(flowId),
      ),
    );

    return { isUsed: isUsedInFlows || isUsedInFilters, allFlowsWithFlowAsSource };
  };

  return (
    <>
      <Box
        sx={{
          paddingTop: '0.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h6"
          align="left"
          sx={{
            paddingInline: '0.5rem',
            fontSize: fontSize.large,
            fontWeight: 600,
            color: 'primary.contrastText',
          }}
        >
          Results
          <Typography
            component="span"
            sx={{
              textAlign: 'center',
              fontSize: '11px',
              color: '#999999',
              marginLeft: '0.3rem',
            }}
          >
            {selectedFlow?.source?.length} of 1
          </Typography>
        </Typography>
        <Tooltip
          title="Add Result"
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '12px',
              },
            },
          }}
        >
          <IconButton
            onClick={(e: any) => {
              if (disable) return;
              setAnchorEl(e.currentTarget);
            }}
            sx={{ padding: 0, marginRight: '0.3rem' }}
            disabled={selectedFlow?.condition === 'Else'}
          >
            <AddCircleOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
        {valueTypes.map((type, index) => {
          return (
            <MenuItem
              key={index}
              sx={{ width: '7rem', fontSize: fontSize.small, color: palette.primary.contrastText }}
              disabled={!safeToAddType(type)}
              value={type}
              onClick={() => {
                if (disable) return;
                if (!selectedResultType || onlyConditionWithResultField) {
                  dispatch(
                    setResultTypeForIfElse(propKey, currentCalculationSession.activeFlow!, type),
                  );
                }
                dispatch(
                  addResultForIfElse(
                    propKey,
                    currentCalculationSession.activeFlow!,
                    conditionNumber!,
                    type === 'date'
                      ? new Date().toISOString().split('T')[0]
                      : type === 'boolean'
                        ? 'false'
                        : type === 'text'
                          ? ''
                          : '0',
                    type,
                  ),
                );
                setAnchorEl(null);
              }}
            >
              {type.replace(/\b\w/g, (char) => char.toUpperCase())}
            </MenuItem>
          );
        })}
      </Menu>
      {/* <Divider /> */}
      <ResultDropZone>
        {hasSource ? (
          <ResultCard
            key={selectedFlow.source[0]}
            sourceId={selectedFlow.source[0] as string}
            propKey={propKey}
            sourceType={selectedFlow.sourceType[0] as string}
            disabled={disable}
            isFlowUsedInOtherFlowsAsSource={isFlowUsedInOtherFlowsAsSource(activeFlow).isUsed}
          />
        ) : null}
      </ResultDropZone>
    </>
  );
};

export default Result;
